import React, { useEffect, useMemo, useState } from "react";
import "./WealthSeriesPortfolioInfo.scss";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import AtlasLogo from "../../../../assets/images/wealth-series/atlas-logo.png";
import TitanLogo from "../../../../assets/images/wealth-series/titan-logo.png";
import EverestLogo from "../../../../assets/images/wealth-series/everest-logo.png";
import AtlasLogoDark from "../../../../assets/images/wealth-series/atlas-logo-dark.png";
import TitanLogoDark from "../../../../assets/images/wealth-series/titan-logo-dark.png";
import EverestLogoDark from "../../../../assets/images/wealth-series/everest-logo-dark.png";
import CoreLogo from "../../../../assets/images/wealth-series/core-logo.png";
import CoreLogoDark from "../../../../assets/images/wealth-series/core-logo-dark.png";
import { useHistory, useParams } from "react-router-dom";
import {
  Backdrop,
  IconButton,
  Menu,
  MenuItem,
  styled,
  Tooltip,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import LockIcon from "@mui/icons-material/Lock";
import { connect } from "react-redux";
import { portfolioLoad } from "../../../../appRedux/ducks/wealthPortfolio";
import { portfolioHashMap } from "../WealthSeriesPortfolioTickers/tickerData";
import BeautifulSkeleton from "../../../common/Skeletons/BeautifulSkeleton";
import {
  fetchSubscribedPortfoliosRequest,
  portfolioSubscriptionRequest,
} from "../../../../appRedux/ducks/portfolioSubscription";
import { useAuth } from "../../../../contexts/Auth";

const WealthSeriesPortfolioInfo = ({
  themeMode,
  fetchPortfolioData: _fetchPortfolioData,
  wealthPortfolios: _wealthPortfolios,
  loadingPortfolio,
  fetchSubscribed,
  handleSubscription,
  loadingSub,
  subscribedPortfolios,
  isMobile = false,
  lastUpdated,
  portfolioData: _portfolioData,
}) => {
  const {
    hasWealthSeriesAtlas,
    hasWealthSeriesTitan,
    hasWealthSeriesEverest,
    hasWealthSeriesCore,
    hasWealthSeriesAtlasAuto,
    hasWealthSeriesTitanAuto,
    hasWealthSeriesEverestAuto,
    userDataLoading: _userDataLoading,
  } = useAuth();

  const { portfolio, series } = useParams();
  const [selectedPortfolio, setSelectedPortfolio] = useState(portfolio);
  const history = useHistory();

  useEffect(() => {
    fetchSubscribed();
  }, []);

  // Helper function to determine if auto portfolio should be used
  const shouldUseAutoPortfolio = useMemo(() => {
    if (series === "core") return false; // Core series has no auto portfolios
    if (series === "atlas") return hasWealthSeriesAtlasAuto;
    if (series === "titan") return hasWealthSeriesTitanAuto;
    if (series === "everest") return hasWealthSeriesEverestAuto;
    return false;
  }, [
    series,
    hasWealthSeriesAtlasAuto,
    hasWealthSeriesTitanAuto,
    hasWealthSeriesEverestAuto,
  ]);

  // Get the appropriate portfolio ID based on user permissions
  const getPortfolioId = (portfolioName) => {
    if (shouldUseAutoPortfolio) {
      // Try to use auto version if available
      const autoPortfolioName = `${portfolioName}_auto`;
      const autoPortfolioId = portfolioHashMap[autoPortfolioName];
      if (autoPortfolioId) {
        return autoPortfolioId;
      }
    }
    // Fallback to regular portfolio
    return portfolioHashMap[portfolioName];
  };
  const findLabelByPortfolioId = (portfolioId, tierData) => {
    if (!portfolioId || !Array.isArray(tierData)) return "";

    for (const tier of tierData) {
      if (!tier.values) continue; // Guard against missing or undefined values

      const foundValue = tier.values.find(
        (value) =>
          value.id === portfolioHashMap[portfolioId] ||
          (value.autoId &&
            value.autoId === portfolioHashMap[`${portfolioId}_auto`]),
      );
      if (foundValue) {
        return foundValue.label;
      }
    }

    return "";
  };

  const portfolioDatabaseId = getPortfolioId(selectedPortfolio);
  const isSubscribed = subscribedPortfolios.includes(portfolioDatabaseId);

  const tierData = useMemo(() => {
    return [
      {
        logo: themeMode === "light" ? AtlasLogo : AtlasLogoDark,
        name: "Atlas",
        hasAccess: hasWealthSeriesAtlas,
        hasAutoAccess: hasWealthSeriesAtlasAuto,
        values: [
          {
            name: "atlas_growth",
            label: "Accelerated Growth",
            id: portfolioHashMap["atlas_growth"],
            autoId: hasWealthSeriesAtlasAuto
              ? portfolioHashMap["atlas_growth_auto"]
              : null,
          },
          {
            name: "atlas_low-risk-volatility",
            label: "Secure Bluechip",
            id: portfolioHashMap["atlas_low-risk-volatility"],
            autoId: hasWealthSeriesAtlasAuto
              ? portfolioHashMap["atlas_low-risk-volatility_auto"]
              : null,
          },
          {
            name: "atlas_fundamental",
            label: "Undervalued Advantage",
            id: portfolioHashMap["atlas_fundamental"],
            autoId: hasWealthSeriesAtlasAuto
              ? portfolioHashMap["atlas_fundamental_auto"]
              : null,
          },
        ],
      },
      {
        logo: themeMode === "light" ? TitanLogo : TitanLogoDark,
        name: "Titan",
        hasAccess: hasWealthSeriesTitan,
        hasAutoAccess: hasWealthSeriesTitanAuto,
        values: [
          {
            name: "titan_growth",
            label: "Dynamic Upside",
            id: portfolioHashMap["titan_growth"],
            autoId: hasWealthSeriesTitanAuto
              ? portfolioHashMap["titan_growth_auto"]
              : null,
          },
          {
            name: "titan_low-risk-volatility",
            label: "Steady Guard",
            id: portfolioHashMap["titan_low-risk-volatility"],
            autoId: hasWealthSeriesTitanAuto
              ? portfolioHashMap["titan_low-risk-volatility_auto"]
              : null,
          },
          {
            name: "titan_fundamental",
            label: "Fundamental Edge",
            id: portfolioHashMap["titan_fundamental"],
            autoId: hasWealthSeriesTitanAuto
              ? portfolioHashMap["titan_fundamental_auto"]
              : null,
          },
        ],
      },
      {
        logo: themeMode === "light" ? EverestLogo : EverestLogoDark,
        name: "Everest",
        hasAccess: hasWealthSeriesEverest,
        hasAutoAccess: hasWealthSeriesEverestAuto,
        values: [
          {
            name: "everest_growth",
            label: "Momentum Ascent",
            id: portfolioHashMap["everest_growth"],
            autoId: hasWealthSeriesEverestAuto
              ? portfolioHashMap["everest_growth_auto"]
              : null,
          },
          {
            name: "everest_low-risk-volatility",
            label: "Resilient Horizon",
            id: portfolioHashMap["everest_low-risk-volatility"],
            autoId: hasWealthSeriesEverestAuto
              ? portfolioHashMap["everest_low-risk-volatility_auto"]
              : null,
          },
          {
            name: "everest_fundamental",
            label: "Hidden Value Discovery",
            id: portfolioHashMap["everest_fundamental"],
            autoId: hasWealthSeriesEverestAuto
              ? portfolioHashMap["everest_fundamental_auto"]
              : null,
          },
          {
            name: "everest_defensive",
            label: "Capital Shield",
            id: portfolioHashMap["everest_defensive"],
            autoId: hasWealthSeriesEverestAuto
              ? portfolioHashMap["everest_defensive_auto"]
              : null,
          },
          {
            name: "everest_balanced",
            label: "Steady Climb",
            id: portfolioHashMap["everest_balanced"],
            autoId: hasWealthSeriesEverestAuto
              ? portfolioHashMap["everest_balanced_auto"]
              : null,
          },
        ],
      },
      {
        logo: themeMode === "light" ? CoreLogo : CoreLogoDark, // TODO: Add proper Core logo
        name: "Core",
        hasAccess: hasWealthSeriesCore,
        hasAutoAccess: false, // Core series has no auto portfolios
        values: [
          {
            name: "core_growth",
            label: "Capital Jetstream",
            id: portfolioHashMap["core_growth"],
            autoId: null, // No auto version for core
          },
          {
            name: "core_low-risk-volatility",
            label: "Safe Harbor",
            id: portfolioHashMap["core_low-risk-volatility"],
            autoId: null, // No auto version for core
          },
          {
            name: "core_balanced",
            label: "Robust Anchor",
            id: portfolioHashMap["core_balanced"],
            autoId: null, // No auto version for core
          },
        ],
      },
    ];
  }, [
    themeMode,
    hasWealthSeriesAtlas,
    hasWealthSeriesTitan,
    hasWealthSeriesEverest,
    hasWealthSeriesCore,
    hasWealthSeriesAtlasAuto,
    hasWealthSeriesTitanAuto,
    hasWealthSeriesEverestAuto,
  ]);

  const menuLabel = useMemo(() => {
    return findLabelByPortfolioId(portfolio, tierData);
  }, [portfolio, tierData]);

  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClickMenuItem = (tier, value) => {
    setSelectedPortfolio(value.name);
    history.push(
      `/wealth-series/${tier.name.toLowerCase()}/${value.name}${location.search}`,
    );
    handleClose();
  };

  const CustomBackdrop = styled(Backdrop)({
    '&[aria-hidden="true"]': {
      inert: true,
    },
  });
  const getTierAccess = () => {
    history.push("/wealth-series-signup");
  };

  const logoSrc =
    themeMode === "light"
      ? series === "atlas"
        ? AtlasLogo
        : series === "everest"
          ? EverestLogo
          : series === "core"
            ? CoreLogo
            : TitanLogo
      : series === "atlas"
        ? AtlasLogoDark
        : series === "everest"
          ? EverestLogoDark
          : series === "core"
            ? CoreLogoDark
            : TitanLogoDark;

  return (
    <div className="ws-portfolio-info">
      <div className="ws-portfolio-info-header">
        <div className="ws-portfolio-info-tier">
          <img
            src={logoSrc}
            alt={`${series.charAt(0).toUpperCase() + series.slice(1)}`}
            className={`ws-portfolio-info-tier-logo ${series}`}
          />
          <div className="ws-portfolio-info-tier-label" onClick={handleClick}>
            <span>{menuLabel}</span>
            {open ? (
              <ArrowDropUpIcon
                style={
                  themeMode === "light"
                    ? { color: "#007BFF" }
                    : { color: "#009EFF" }
                }
              />
            ) : (
              <ArrowDropDownIcon
                style={
                  themeMode === "light" ? { color: "#000" } : { color: "#FFF" }
                }
              />
            )}
          </div>

          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            className="ws-portfolio-info-tier-menu"
            disableAutoFocus
            disableRestoreFocus
            slots={{
              backdrop: CustomBackdrop,
            }}
          >
            {tierData.map((tier, index) => (
              <div
                className="ws-portfolio-tier-menu-wrapper"
                key={`tier-wrapper-${index}`}
              >
                <MenuItem
                  className={`ws-portfolio-tier-menu-logo-item ${tier.hasAccess || tier.hasAutoAccess ? "has-access" : ""}`}
                  onClick={() => {
                    if (!tier.hasAccess && !tier.hasAutoAccess) {
                      getTierAccess(tier.name);
                    }
                  }}
                >
                  <img
                    src={tier.logo}
                    alt={`${tier.name} Logo`}
                    className="ws-portfolio-tier-logo"
                    style={
                      tier.name === "Everest" ? { width: "85px" } : undefined
                    }
                  />

                  {!tier.hasAccess && !tier.hasAutoAccess && (
                    <div className="ws-portfolio-tier-no-access">
                      <LockIcon />
                      <div className="ws-portfolio-tier-no-access-text">
                        Get access
                      </div>
                    </div>
                  )}
                </MenuItem>

                {tier.values.map((value, index1) => (
                  <MenuItem
                    key={`tier-value-${tier.name}-${index1}`}
                    className="ws-portfolio-tier-menu-item"
                    onClick={() => handleClickMenuItem(tier, value)}
                    selected={value.name === selectedPortfolio}
                    disabled={!tier.hasAccess && !tier.hasAutoAccess}
                  >
                    <div className="ws-portfolio-tier-value-label">
                      {value.label}
                    </div>
                  </MenuItem>
                ))}
              </div>
            ))}
          </Menu>
        </div>

        <Tooltip
          placement="top"
          arrow
          title={isSubscribed ? "Unsubscribe from alerts" : "Get email alerts"}
        >
          <IconButton
            disabled={loadingSub}
            className="ws-portfolio-info-mail-icon"
            onClick={() => {
              handleSubscription(portfolioDatabaseId, !isSubscribed);
            }}
          >
            {isSubscribed ? <MarkEmailReadIcon /> : <MailOutlineIcon />}
          </IconButton>
        </Tooltip>
      </div>

      {isMobile && (
        <div className="ws-portfolio-header__timestamp">
          {loadingPortfolio ? (
            <BeautifulSkeleton width={350} height={20} />
          ) : (
            lastUpdated
          )}
        </div>
      )}
    </div>
  );
};

const stateToProps = (state) => ({
  themeMode: state.themeMode.themeMode,
  wealthPortfolios: state.wealthPortfolio,
  loadingPortfolio: state.wealthPortfolio.loading,
  companyDataLoading: state.companyData.companyDataLoading,
  tickerDetails: state.companyData.companyData.tickerDetails,
  loadingSub: state.portfolioSubscribe.loading,
  subscribedPortfolios: state.portfolioSubscribe.subscribedPortfolios,
  _portfolioLastUpdated: state.wealthPortfolio.lastUpdated,
});

const dispatchToProps = (dispatch) => ({
  fetchPortfolioData: (id, addSparkline = true, forceRefresh = false) =>
    dispatch(portfolioLoad(id, addSparkline, forceRefresh)),
  handleSubscription: (id, subscribe) =>
    dispatch(portfolioSubscriptionRequest(id, subscribe)),
  fetchSubscribed: () => dispatch(fetchSubscribedPortfoliosRequest()),
});

export default connect(
  stateToProps,
  dispatchToProps,
)(WealthSeriesPortfolioInfo);
